import { Lock, Plus, Trophy, Unlock, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { CategoryName, ComparisonResponse, ComparisonRow } from '../types/api';
import { describePercentile, formatPence, scoreTone, toneColour } from '../lib/format';
import { num } from '../lib/values';
import VerdictBadge from './VerdictBadge';
import { tx } from '../i18n';
import { CATEGORY_ORDER, categoryShort } from '../lib/report';

interface ComparisonTableProps {
  comparison: ComparisonResponse;
  onRemove: (propertyAddressId: string) => void;
  /** Open the unlock options for one locked home. */
  onUnlock: (row: ComparisonRow) => void;
}

/**
 * How far ahead the best home is in a category, among the homes whose score we actually have.
 *
 * <p>A trophy says who won; it does not say whether winning meant anything. Two points apart is
 * noise and twelve is a decision, and the reader cannot tell which without the gap - so the gap is
 * shown next to the trophy, and only when there is a second score to measure it against.
 */
function leadBy(rows: ComparisonRow[], category: CategoryName): number | null {
  const scores = rows
    .map((row) => num(row.categories.find((cell) => cell.category === category)?.score))
    .filter((score): score is number => score !== null)
    .sort((a, b) => b - a);
  const best = scores[0];
  const second = scores[1];
  return best === undefined || second === undefined ? null : best - second;
}

/**
 * Locked and unlocked homes in one table.
 *
 * <p>A locked cell shows the summary sentence and a price, never a blurred number: there is no
 * number to blur, because the API did not send one. Leader badges only appear among unlocked homes,
 * and the table says so when that makes the comparison partial.
 */
export default function ComparisonTable({ comparison, onRemove, onUnlock }: ComparisonTableProps) {
  // A fixed set of columns, so a row scored before compliance was switched off still lines up.
  const categories = CATEGORY_ORDER.map((category) => ({ category, label: categoryShort(category) }));
  // Each column's name, carried on its cells, so the table can become one card per home on a
  // phone without the numbers losing what they are numbers of.
  const overallLabel = tx('Overall', 'Genel', 'Ogółem', 'Global', 'Global');
  const costLabel = tx(
    'Running cost',
    'Giderler',
    'Koszty eksploatacji',
    'Custo de utilização',
    'Coste de la vivienda',
  );
  const singlePrice = comparison.unlockOptions.find(
    (option) => option.productCode === 'SINGLE_DETAIL',
  );
  const gaps = new Map(categories.map(({ category }) => [category, leadBy(comparison.rows, category)]));
  const alone = comparison.rows.length === 1;
  // Winning a race you ran alone is not a finding. With one home there are no leaders to mark.
  const showLeaders = !alone;

  return (
    <div className="stack stack--tight">
      <div className="table-wrap">
        <table className="compare">
          <thead>
            <tr>
              <th scope="col">{tx('Home', 'Konut',
    'Mieszkanie',
    'Casa',
    'Vivienda')}</th>
              <th scope="col">{overallLabel}</th>
              {categories.map((cell) => (
                <th key={cell.category} scope="col">
                  {cell.label}
                </th>
              ))}
              <th scope="col">{costLabel}</th>
              <th scope="col">
                <span className="sr-only">{tx('Remove', 'Çıkar',
    'Usuń',
    'Remover',
    'Quitar')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {comparison.rows.map((row) => {
              const isOverallLeader = showLeaders && comparison.leaders.OVERALL === row.propertyAddressId;
              const overall = num(row.overallScore);
              return (
                <tr key={row.propertyAddressId}>
                  <th scope="row" className="compare__home">
                    <div className="compare__address">{shortAddress(row.addressLine)}</div>
                    <div className="compare__meta">
                      {row.postcode} &middot; {row.borough}
                    </div>
                    <div className="compare__home-actions">
                      {row.analysisId ? (
                        <Link to={`/report/${row.analysisId}`} className="compare__link">
                          {tx('Report', 'Rapor',
    'Raport',
    'Relatório',
    'Informe')}
                        </Link>
                      ) : null}
                      {!row.detailed ? (
                        <button
                          type="button"
                          className="compare__link"
                          onClick={() => onUnlock(row)}
                        >
                          <Unlock size={12} aria-hidden="true" />
                          {tx('Unlock scores', 'Puanların kilidini aç',
    'Odblokuj oceny',
    'Desbloquear pontuações',
    'Desbloquear puntuaciones')}
                          {singlePrice ? ` · ${formatPence(singlePrice.amountPence)}` : ''}
                        </button>
                      ) : null}
                    </div>
                  </th>

                  <td data-label={overallLabel} className={isOverallLeader ? 'is-best' : undefined}>
                    {overall === null ? (
                      <span className="muted">
                        {tx('Not scored yet', 'Henüz puanlanmadı', 'Jeszcze nieocenione', 'Ainda sem pontuação', 'Aún sin puntuar')}
                      </span>
                    ) : (
                      <>
                        <div
                          className="compare__score"
                          style={{ color: toneColour(scoreTone(overall)) }}
                        >
                          {overall}
                        </div>
                        {row.label ? (
                          <div style={{ marginTop: 4 }}>
                            <VerdictBadge label={row.label} size="sm" />
                          </div>
                        ) : null}
                        {isOverallLeader ? (
                          <div style={{ marginTop: 6 }}>
                            <span className="leader">
                              <Trophy size={11} aria-hidden="true" />
                              {tx('Best overall', 'Genelde en iyi',
    'Najlepszy ogółem',
    'Melhor no conjunto',
    'Mejor en conjunto')}
                            </span>
                          </div>
                        ) : null}
                      </>
                    )}
                  </td>

                  {categories.map(({ category, label }) => {
                    const cell = row.categories.find((entry) => entry.category === category);
                    if (!cell) {
                      return (
                        <td key={category} data-label={label}>
                          <span className="compare__meta">{tx('Not assessed', 'Değerlendirilmedi',
    'Nieocenione',
    'Não avaliado',
    'Sin evaluar')}</span>
                        </td>
                      );
                    }
                    const isLeader = showLeaders && comparison.leaders[cell.category] === row.propertyAddressId;
                    const score = num(cell.score);
                    const rank = describePercentile(cell.percentile);
                    const gap = gaps.get(category) ?? null;
                    return (
                      <td
                        key={cell.category}
                        data-label={label}
                        className={isLeader ? 'is-best' : undefined}
                      >
                        {cell.rated === false ? (
                          <span className="compare__meta">{tx('Data could not be retrieved', 'Veri alınamadı',
    'Nie udało się pobrać danych',
    'Não foi possível obter dados',
    'No se han podido obtener datos')}</span>
                        ) : score === null ? (
                          <span className="compare__locked" title={tx('Score unlocks with the full report', 'Puan tam raporla açılır',
    'Ocena odblokowuje się z pełnym raportem',
    'A pontuação desbloqueia com o relatório completo',
    'La puntuación se desbloquea con el informe completo')}>
                            <Lock size={11} aria-hidden="true" />
                            {tx('Score locked', 'Puan kilitli',
    'Ocena zablokowana',
    'Pontuação bloqueada',
    'Puntuación bloqueada')}
                          </span>
                        ) : (
                          <>
                            <div
                              className="compare__score"
                              style={{ color: toneColour(scoreTone(score)) }}
                            >
                              {score}
                            </div>
                            {/*
                              * The ranking sentence is written only when there is a ranking. The
                              * server leaves a null property out of the JSON altogether, so a cell
                              * with no regional rank arrived here as undefined, sailed past a
                              * `!== null` test and printed "Better than undefined% of London".
                              */}
                            <div className="compare__meta">
                              {rank
                                ?? tx(
                                  'Regional comparison unavailable',
                                  'Bölgesel karşılaştırma yok',
                                  'Brak porównania regionalnego',
                                  'Sem comparação regional',
                                  'Sin comparación regional',
                                )}
                            </div>
                            {isLeader ? (
                              <div style={{ marginTop: 4 }}>
                                <span className="leader">
                                  <Trophy size={12} aria-hidden="true" />
                                  {gap !== null && gap > 0
                                    ? tx(
                                        `Best · ${gap} ahead`,
                                        `En iyi · ${gap} puan önde`,
                                        `Najlepszy · o ${gap} więcej`,
                                        `Melhor · ${gap} à frente`,
                                        `Mejor · ${gap} por delante`,
                                      )
                                    : tx('Best', 'En iyi', 'Najlepszy', 'Melhor', 'Mejor')}
                                </span>
                              </div>
                            ) : null}
                          </>
                        )}
                        <p className="compare__cell-summary">
                          {cell.summary}
                        </p>
                      </td>
                    );
                  })}

                  <td data-label={costLabel}>
                    {row.detailed ? (
                      <span className="compare__score" style={{ fontSize: '1rem' }}>
                        {num(row.runningCostPence) === null
                          ? tx('Not published', 'Yayımlanmamış',
    'Nieopublikowane',
    'Não publicado',
    'No publicado')
                          : tx(`${formatPence(row.runningCostPence)}/yr`, `${formatPence(row.runningCostPence)}/yıl`,
    `${formatPence(row.runningCostPence)}/rok`,
    `${formatPence(row.runningCostPence)}/ano`,
    `${formatPence(row.runningCostPence)}/año`)}
                      </span>
                    ) : (
                      <span className="compare__locked">
                        <Lock size={11} aria-hidden="true" />
                        {tx('Locked', 'Kilitli',
    'Zablokowane',
    'Bloqueado',
    'Bloqueado')}
                      </span>
                    )}
                  </td>

                  <td className="compare__remove-cell">
                    <button
                      type="button"
                      className="compare__remove"
                      onClick={() => onRemove(row.propertyAddressId)}
                      aria-label={tx(`Remove ${row.addressLine} from the comparison`, `${row.addressLine} adresini karşılaştırmadan çıkar`,
    `Usuń ${row.addressLine} z porównania`,
    `Remover ${row.addressLine} da comparação`,
    `Quitar ${row.addressLine} de la comparación`)}
                      title={tx('Remove from comparison', 'Karşılaştırmadan çıkar',
    'Usuń z porównania',
    'Remover da comparação',
    'Quitar de la comparación')}
                    >
                      <X size={15} aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              );
            })}

            {/*
              * One home is not a comparison, and a table with a single row quietly pretends it is.
              * The empty slot says out loud what is missing and offers the one action that fixes
              * it, in the place the second home will appear.
              */}
            {alone ? (
              <tr className="compare__empty-row">
                <td colSpan={categories.length + 4}>
                  <div className="compare__empty">
                    <Plus size={22} aria-hidden="true" className="compare__empty-icon" />
                    <div className="compare__empty-text">
                      <p className="compare__empty-title">
                        {tx(
                          'Add another home to compare',
                          'Karşılaştırmak için başka bir konut ekleyin',
                          'Dodaj kolejne mieszkanie do porównania',
                          'Junte outra casa para comparar',
                          'Añade otra vivienda para comparar',
                        )}
                      </p>
                      <p className="small muted">
                        {tx(
                          'With one home there is nothing to weigh it against. Add a second and every category gets a winner and a margin.',
                          'Tek konutla kıyaslanacak bir şey yok. İkincisini ekleyin; her kategori bir kazanan ve bir fark gösterir.',
                          'Przy jednym mieszkaniu nie ma go z czym zestawić. Dodaj drugie, a każda kategoria dostanie zwycięzcę i przewagę.',
                          'Com uma só casa não há com que a comparar. Junte uma segunda e cada categoria ganha um vencedor e uma margem.',
                          'Con una sola vivienda no hay con qué compararla. Añade una segunda y cada categoría tendrá un ganador y una diferencia.',
                        )}
                      </p>
                    </div>
                    <Link to="/" className="button">
                      {tx('Search a postcode', 'Posta kodu ara', 'Wyszukaj kod pocztowy', 'Pesquisar um código postal', 'Buscar un código postal')}
                    </Link>
                  </div>
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {comparison.leadersRestricted ? (
        <p className="tiny muted">
          {tx(
            'Best-in-category badges are chosen only from homes you have unlocked. Comparing partly locked homes on numbers we have not shown you would be misleading, so we do not do it.',
            'Kategori birincisi rozetleri yalnızca kilidi açılmış konutlar arasından seçilir. Size göstermediğimiz rakamlarla kısmen kilitli konutları kıyaslamak yanıltıcı olurdu.',
    'Odznaki „najlepszy w kategorii” przyznajemy tylko wśród mieszkań, które odblokowałeś. Porównywanie częściowo zablokowanych mieszkań według liczb, których ci nie pokazaliśmy, wprowadzałoby w błąd, więc tego nie robimy.',
    'Os distintivos de melhor da categoria são escolhidos apenas entre as casas que desbloqueou. Comparar casas parcialmente bloqueadas com números que não lhe mostrámos seria enganador, por isso não o fazemos.',
    'Las insignias de mejor de la categoría se eligen solo entre las viviendas que has desbloqueado. Comparar viviendas parcialmente bloqueadas con cifras que no te hemos enseñado sería engañoso, así que no lo hacemos.',
          )}
        </p>
      ) : null}
    </div>
  );
}

/** "Flat 1, 24 Brune Street, London, E1 6AN" becomes "Flat 1, 24 Brune Street". */
function shortAddress(addressLine: string): string {
  const parts = addressLine.split(',').map((part) => part.trim());
  return (parts.length > 2 ? parts.slice(0, -2) : parts).join(', ');
}
